import { nanoid } from 'nanoid'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { updateUser } from '@/client/sdk.gen'
import type { ProductTourState } from '@/client/types.gen'
import { useAuth } from '@/context/AuthContext'
import { PRODUCT_TOUR_SESSION_PENDING } from '@/lib/product-tour/constants'
import {
	SAMPLE_PASTE_FILENAME,
	SAMPLE_PROCESS_DESCRIPTION_TEXT,
} from '@/lib/product-tour/samplePasteText'
import { PRODUCT_TOUR_STEP_COUNT } from '@/lib/product-tour/stepIndices'

export type ProductTourPastePrefill = {
	text: string
	filename: string
}

export type ProductTourContextValue = {
	isTourActive: boolean
	stepIndex: number
	runId: string
	tourProjectId: string | null
	tourAnalysisId: string | null
	pastePrefill: ProductTourPastePrefill | null
	startTour: (options?: { fromHelp?: boolean }) => void
	exitTour: (reason: 'completed' | 'skipped') => void
	goToStep: (index: number) => void
	nextStep: () => void
	setTourProjectId: (id: string | null) => void
	setTourAnalysisId: (id: string | null) => void
	requestPastePrefill: () => void
	clearPastePrefill: () => void
}

const ProductTourContext = createContext<ProductTourContextValue | null>(null)

function readSessionPending(): boolean {
	try {
		return sessionStorage.getItem(PRODUCT_TOUR_SESSION_PENDING) === '1'
	} catch {
		return false
	}
}

function clearSessionPending(): void {
	try {
		sessionStorage.removeItem(PRODUCT_TOUR_SESSION_PENDING)
	} catch {
		/* ignore */
	}
}

/** Clears Help → tour handoff flag (call when starting the tour from /projects). */
export function clearProductTourSessionPending(): void {
	clearSessionPending()
}

export function ProductTourProvider({ children }: { children: ReactNode }) {
	const { user, login } = useAuth()
	const [isTourActive, setIsTourActive] = useState(false)
	const [stepIndex, setStepIndex] = useState(0)
	const [runId, setRunId] = useState('')
	const [tourProjectId, setTourProjectId] = useState<string | null>(null)
	const [tourAnalysisId, setTourAnalysisId] = useState<string | null>(null)
	const [pastePrefill, setPastePrefill] = useState<ProductTourPastePrefill | null>(null)
	const backendTourState: ProductTourState = user?.product_tour_state ?? 'not_started'

	const persistTourState = useCallback(
		async (state: ProductTourState) => {
			if (!user?.id) return
			try {
				const response = await updateUser({
					path: { user_id: user.id.toString() },
					body: { product_tour_state: state },
				})
				const updatedUser = response.data?.user
				if (response.data?.success && updatedUser?.product_tour_state != null) {
					login({
						...user,
						product_tour_state: updatedUser.product_tour_state,
					})
				}
			} catch {
				/* ignore - backend remains source of truth on next profile refresh */
			}
		},
		[user, login]
	)

	const exitTour = useCallback(
		(reason: 'completed' | 'skipped') => {
			setIsTourActive(false)
			setStepIndex(0)
			setTourProjectId(null)
			setTourAnalysisId(null)
			setPastePrefill(null)
			void persistTourState(reason === 'completed' ? 'finished' : 'skipped')
			clearSessionPending()
		},
		[persistTourState]
	)

	const startTour = useCallback(
		(options?: { fromHelp?: boolean }) => {
			if (options?.fromHelp) {
				try {
					sessionStorage.setItem(PRODUCT_TOUR_SESSION_PENDING, '1')
				} catch {
					/* ignore */
				}
			}
			setRunId(nanoid(6))
			setTourProjectId(null)
			setTourAnalysisId(null)
			setPastePrefill(null)
			setStepIndex(0)
			setIsTourActive(true)
			if (
				backendTourState === 'not_started' ||
				backendTourState === 'skipped' ||
				backendTourState === 'finished'
			) {
				void persistTourState('started')
			}
		},
		[backendTourState, persistTourState]
	)

	const goToStep = useCallback((index: number) => {
		setStepIndex(Math.max(0, Math.min(PRODUCT_TOUR_STEP_COUNT - 1, index)))
	}, [])

	const nextStep = useCallback(() => {
		setStepIndex((prev) => {
			const next = prev + 1
			if (next >= PRODUCT_TOUR_STEP_COUNT) {
				return prev
			}
			return next
		})
	}, [])

	const requestPastePrefill = useCallback(() => {
		setPastePrefill({
			text: SAMPLE_PROCESS_DESCRIPTION_TEXT,
			filename: SAMPLE_PASTE_FILENAME,
		})
	}, [])

	const clearPastePrefill = useCallback(() => {
		setPastePrefill(null)
	}, [])

	const value = useMemo(
		(): ProductTourContextValue => ({
			isTourActive,
			stepIndex,
			runId,
			tourProjectId,
			tourAnalysisId,
			pastePrefill,
			startTour,
			exitTour,
			goToStep,
			nextStep,
			setTourProjectId,
			setTourAnalysisId,
			requestPastePrefill,
			clearPastePrefill,
		}),
		[
			isTourActive,
			stepIndex,
			runId,
			tourProjectId,
			tourAnalysisId,
			pastePrefill,
			startTour,
			exitTour,
			goToStep,
			nextStep,
			requestPastePrefill,
			clearPastePrefill,
		]
	)

	return <ProductTourContext.Provider value={value}>{children}</ProductTourContext.Provider>
}

export function useProductTour(): ProductTourContextValue {
	const ctx = useContext(ProductTourContext)
	if (!ctx) {
		throw new Error('useProductTour must be used within ProductTourProvider')
	}
	return ctx
}

export function useProductTourOptional(): ProductTourContextValue | null {
	return useContext(ProductTourContext)
}

export { PRODUCT_TOUR_STEP_COUNT } from '@/lib/product-tour/stepIndices'

/** Mark session pending so next /projects visit starts the tour (Help page). */
export function setProductTourSessionPending(): void {
	try {
		sessionStorage.setItem(PRODUCT_TOUR_SESSION_PENDING, '1')
	} catch {
		/* ignore */
	}
}

/**
 * Auto-start on /projects when Help set a pending flag (always), or backend tour state is not_started.
 */
export function shouldAutoStartProductTourOnProjects(
	userId: string | undefined,
	productTourState: ProductTourState | undefined
): boolean {
	if (!userId) return false
	// Help → /projects: honor pending before mobile/reduced-motion so the tour can start
	if (readSessionPending()) return true
	if (typeof window === 'undefined' || !window.matchMedia) return false
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
	if (window.matchMedia('(max-width: 767px)').matches) return false
	return (productTourState ?? 'not_started') === 'not_started'
}
